import {
  getCurrentDateRange,
  DATE_MODES,
  getMonthDateRange,
  getWeekDateRange,
  getYearDateRange,
  getCustomDateRange,
  getAllDateRange,
  parseDateString
} from './dateUtils';

/**
 * Validate date string format (YYYY-MM-DD)
 */
const isValidDateString = (str) => {
  if (!str || typeof str !== 'string') return false;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(str)) return false;
  const date = new Date(str);
  return !isNaN(date.getTime());
};

/**
 * Parse date range from URL search params
 * Format: ?start=2025-01-01&end=2025-01-31&mode=month
 */
export const parseDateRangeFromUrl = (searchParams) => {
  const start = searchParams.get('start');
  const end = searchParams.get('end');
  const mode = searchParams.get('mode') || DATE_MODES.MONTH;

  if (mode === DATE_MODES.ALL) {
    return getAllDateRange();
  }

  if (isValidDateString(start) && isValidDateString(end)) {
    switch (mode) {
      case DATE_MODES.MONTH: {
        const startDate = parseDateString(start);
        return getMonthDateRange(startDate.getFullYear(), startDate.getMonth());
      }
      case DATE_MODES.WEEK: {
        const startDate = parseDateString(start);
        return getWeekDateRange(startDate);
      }
      case DATE_MODES.YEAR: {
        const startDate = parseDateString(start);
        return getYearDateRange(startDate.getFullYear());
      }
      case DATE_MODES.CUSTOM: {
        return getCustomDateRange(parseDateString(start), parseDateString(end));
      }
      default:
        return getCurrentDateRange(DATE_MODES.MONTH);
    }
  }

  return getCurrentDateRange(DATE_MODES.MONTH);
};

/**
 * Serialize date range to URL params
 */
export const serializeDateRangeToUrl = (dateRange) => {
  if (dateRange?.mode === DATE_MODES.ALL) {
    return { start: '', end: '', mode: DATE_MODES.ALL };
  }

  if (!dateRange || !dateRange.start_date || !dateRange.end_date) {
    const defaultRange = getCurrentDateRange(DATE_MODES.MONTH);
    return {
      start: defaultRange.start_date,
      end: defaultRange.end_date,
      mode: defaultRange.mode
    };
  }

  return {
    start: dateRange.start_date,
    end: dateRange.end_date,
    mode: dateRange.mode
  };
};

/**
 * Parse all filters from URL search params
 * New format: ?start=...&end=...&mode=...&filters=[{entity,column,operator,values}...]
 *
 * @param {URLSearchParams} searchParams
 * @returns {{ filters: Array, dateRange: Object }}
 */
export const parseFiltersFromUrl = (searchParams) => {
  let filters = [];
  let filterGroups = [];

  const filtersParam = searchParams.get('filters');
  if (filtersParam) {
    try {
      const parsed = JSON.parse(filtersParam);
      if (Array.isArray(parsed)) {
        filters = parsed;
      }
    } catch (e) {
      // Invalid JSON — ignore and use empty array
    }
  }

  const groupsParam = searchParams.get('groups');
  if (groupsParam) {
    try {
      const parsed = JSON.parse(groupsParam);
      if (Array.isArray(parsed)) {
        let idCounter = Date.now();
        filterGroups = parsed.map(g => ({
          ...g,
          id: g.id || String(idCounter++)
        }));
      }
    } catch (e) {
      // Invalid JSON — ignore
    }
  }

  return {
    filters,
    filterGroups,
    dateRange: parseDateRangeFromUrl(searchParams)
  };
};

/**
 * Serialize filters to URL search params string
 * New format: single `filters` JSON param for filter array
 *
 * @param {Array} selectedFilters - Array of { entity, column, operator, values }
 * @param {Object} dateRange - Date range object
 * @returns {string} URL search params string
 */
export const serializeFiltersToUrl = (selectedFilters, dateRange, filterGroups = []) => {
  // Preserve existing non-filter params (e.g. movement_id) from the current URL
  const params = new URLSearchParams(window.location.search);

  if (dateRange) {
    const { start, end, mode } = serializeDateRangeToUrl(dateRange);
    params.set('start', start);
    params.set('end', end);
    params.set('mode', mode);
  }

  if (Array.isArray(selectedFilters) && selectedFilters.length > 0) {
    params.set('filters', JSON.stringify(selectedFilters));
  } else {
    params.delete('filters');
  }

  if (Array.isArray(filterGroups) && filterGroups.length > 0) {
    // Strip ephemeral `id` field before serializing
    const serializable = filterGroups.map(({ id, ...rest }) => rest);
    params.set('groups', JSON.stringify(serializable));
  } else {
    params.delete('groups');
  }

  return params.toString();
};
